<script setup lang="ts">
import dayjs from 'dayjs'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import utc from 'dayjs/plugin/utc'
import zhCN from '@arco-design/web-vue/es/locale/lang/zh-cn'
import enUS from '@arco-design/web-vue/es/locale/lang/en-us'

dayjs.extend(utc)

const { locale } = useI18n()
const localeObj = computed(() => {
  return {
    zh: zhCN,
    en: enUS,
  }[locale.value]
})

const { $request } = useNuxtApp()
const route = useRoute()
const user = useUser()
const queryClient = useQueryClient()
const [dataTimeliness, userInfo, regions] = await Promise.all([
  useRequest<Api.Dim.DataTimeliness>('/dim/info/dataTimeliness', { key: 'dataTimeliness' }),
  useRequest<Api.User.Info>('/user/info', { key: 'userInfo' }),
  useRequest<{ data: Api.User.Region[] }>('/user/regions', { key: 'regions' }),
])

if (dataTimeliness.data.value) {
  user.value.dataTimeliness = dataTimeliness.data.value
}
if (regions.data.value) {
  user.value.regions = regions.data.value.data || []
}

if (import.meta.client) {
  /** 刷新dataTimeliness接口 */
  useQuery({
    queryKey: ['/dim/info/dataTimeliness'],
    queryFn: async () => {
      const res = await $request<Api.Dim.DataTimeliness>('/dim/info/dataTimeliness')
      user.value.dataTimeliness = res
      return res
    },
    refetchOnWindowFocus: true,
  })
}

watch(userInfo.data, (data) => {
  user.value.info = data ?? {
    id: 0,
    name: 'v',
    phone: '13611111111',
    expireInSecond: 1704038400,
    isNewUser: 0,
    userRole: 3,
    permissions: [],
    privileges: [],
    accessToken: '',
  } as unknown as Api.User.Info
  user.value.isLogin = user.value.info.id > 0
}, { immediate: true })
const pageKey = {
  '/monitor': '/tool/videoMonitor',
}
watch(() => user.value.isLogin, () => {
  // 登录状态变化重新请求
  nextTick(() => {
    // 排除特定的 queryKey，包括以 '/trades' 开头和动态的 '/download${type}'
    queryClient.refetchQueries({
      predicate: (query) => {
        const queryKey = query?.queryKey?.[0] || ''
        let isExcluded
      = /^\/trades/.test(queryKey) // 匹配订单相关的请求
      || queryKey === '/member/userCorporateMember'
      || /^\/download/.test(queryKey) // 匹配导出相关的请求
        const excludeSpecKeys = Object.keys(pageKey)
        // 判断接口的key是不是有pageKey的值，有的话就只是需要路由在pageKey的value中时才去重新刷新
        const firstQueryKey = queryKey.split('/')[1]
        if (excludeSpecKeys.includes(`/${firstQueryKey}`)) {
          const { path } = route
          for (let i = 0; i < excludeSpecKeys.length; i++) {
            if (path.includes(pageKey[excludeSpecKeys[i]])) {
              isExcluded = false
              break
            }
            else {
              isExcluded = true
            }
          }
        }
        // 返回 false 的 queryKey 将不会被重新请求
        return !isExcluded
      },
    })
  })
})
</script>

<template>
  <a-config-provider :locale="localeObj" global>
    <NuxtLayout>
      <NuxtPage :keepalive="false" />
      <Login />
      <IdentityExpiredDialog />
    </NuxtLayout>
  </a-config-provider>
</template>
